import { useEffect, useRef, useState } from "react";

/** Unterstützte Aufnahmeformate werden zur Laufzeit geprüft (Safari: mp4, Chrome/Firefox: webm/ogg). */
export function pickAudioMime(): string | null {
  if (typeof MediaRecorder === "undefined") return null;
  for (const type of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"]) {
    if (MediaRecorder.isTypeSupported(type)) return type;
  }
  return null;
}

const MAX_SECONDS = 300;

/**
 * Push-to-talk: Aufnahme startet nur auf bewussten Klick, ist sichtbar, kann abgebrochen werden und gibt
 * das Mikrofon beim Stoppen frei. Keine Dauerüberwachung, keine Sprechererkennung.
 */
export function Recorder({ onRecorded, disabled }: { onRecorded: (blob: Blob) => void; disabled?: boolean }) {
  const [state, setState] = useState<"idle" | "recording" | "error">("idle");
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const chunks = useRef<Blob[]>([]);
  const cancelled = useRef(false);
  const timer = useRef<number | undefined>(undefined);
  const mime = pickAudioMime();

  const release = () => {
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
    if (timer.current) window.clearInterval(timer.current);
  };

  useEffect(() => release, []);

  const start = async () => {
    setError(null);
    if (!mime || !navigator.mediaDevices?.getUserMedia) {
      setState("error");
      setError("Sprachaufnahme wird von diesem Browser nicht unterstützt. Alternativ die Diktierfunktion der Tastatur nutzen.");
      return;
    }
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setState("error");
      setError("Kein Zugriff auf das Mikrofon. Bitte in den Browsereinstellungen erlauben.");
      return;
    }
    chunks.current = [];
    cancelled.current = false;
    const rec = new MediaRecorder(stream.current, { mimeType: mime });
    rec.ondataavailable = (e) => e.data.size > 0 && chunks.current.push(e.data);
    rec.onstop = () => {
      release();
      setState("idle");
      if (!cancelled.current && chunks.current.length > 0) {
        onRecorded(new Blob(chunks.current, { type: mime.split(";")[0] }));
      }
    };
    recorder.current = rec;
    rec.start(1000);
    setSeconds(0);
    setState("recording");
    timer.current = window.setInterval(() => {
      setSeconds((s) => {
        if (s + 1 >= MAX_SECONDS) recorder.current?.stop();
        return s + 1;
      });
    }, 1000);
  };

  const stop = (cancel: boolean) => {
    cancelled.current = cancel;
    recorder.current?.stop();
  };

  if (state === "recording") {
    return (
      <div className="recording" role="status" aria-live="polite">
        <span className="rec-dot" aria-hidden="true" />
        <span>Aufnahme läuft · {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")}</span>
        <button type="button" className="btn primary" onClick={() => stop(false)}>Fertig</button>
        <button type="button" className="btn" onClick={() => stop(true)}>Abbrechen</button>
      </div>
    );
  }

  return (
    <>
      <button type="button" className="btn icon" onClick={start} disabled={disabled} aria-label="Sprachaufnahme starten" title="Sprachaufnahme">
        🎤 <span className="label">Sprechen</span>
      </button>
      {error && <p className="error small">{error}</p>}
    </>
  );
}
